import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Mail, MailOpen, TrendingUp, Calendar, Check, X } from 'lucide-react';
import { useIndustry } from '../context/IndustryContext';
import { getIndustryData } from '../data/industryDataService';
import DealChat from '../components/DealChat';

const timelineIcons = {
  inbound_email: { icon: Mail, bg: 'bg-blue-100', color: 'text-blue-500' },
  outbound_email: { icon: MailOpen, bg: 'bg-blue-100', color: 'text-blue-500' },
  stage_change: { icon: TrendingUp, bg: 'bg-amber-100', color: 'text-amber-500' },
  meeting: { icon: Calendar, bg: 'bg-gray-100', color: 'text-gray-500' },
};

const TakeActionModal = ({ opportunity, detail, onClose, onDone }) => {
  const firstAction = detail.pendingActions?.items?.[0];
  const [emailText, setEmailText] = useState(
    `Hi ${detail.poc.name},\n\nI wanted to follow up on our recent discussion regarding the ${opportunity.accountName} engagement. ${firstAction?.description || ''}\n\nPlease let me know a good time to connect this week.\n\nBest regards`
  );
  const [logToCRM, setLogToCRM] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = () => {
    setSending(true);
    setTimeout(() => {
      setSending(false);
      setSent(true);
      setTimeout(() => {
        onDone();
        onClose();
      }, 1200);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="take-action-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-[580px] max-h-[90vh] overflow-y-auto mx-4" data-testid="modal-content">
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <h2 className="text-xl font-bold text-gray-900">{firstAction?.label || 'Take Action'}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1" data-testid="modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium">{opportunity.accountName}</span>
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-50 text-green-600 text-sm font-bold">{opportunity.dealValue >= 1000000 ? `$${(opportunity.dealValue / 1000000).toFixed(1)}M` : `$${Math.round(opportunity.dealValue / 1000)}K`}</span>
          </div>
          <div className="bg-purple-50/60 border border-purple-100 rounded-xl p-4 space-y-2" data-testid="ai-insight">
            <p className="text-xs font-bold text-gray-800 tracking-wider uppercase">AI INSIGHT</p>
            <p className="text-sm text-gray-700 leading-relaxed">{detail.dealSummary}</p>
          </div>
          <div className="space-y-3" data-testid="modal-contact">
            <p className="text-xs font-bold text-gray-500 tracking-wider uppercase">CONTACT</p>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${detail.poc.avatarColor} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>{detail.poc.avatar}</div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{detail.poc.name}</p>
                <p className="text-xs text-gray-500">{detail.pendingActions?.assignedTo?.role || 'Account Executive'}</p>
              </div>
            </div>
          </div>
          <div className="space-y-3" data-testid="email-draft-section">
            <p className="text-xs font-bold text-gray-500 tracking-wider uppercase">EMAIL DRAFT</p>
            <textarea value={emailText} onChange={(e) => setEmailText(e.target.value)} className="w-full min-h-[180px] p-4 text-sm text-gray-700 border border-gray-200 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent leading-relaxed" data-testid="email-draft-textarea" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="log-to-crm">
            <div onClick={() => setLogToCRM(!logToCRM)} className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${logToCRM ? 'bg-blue-600' : 'bg-white border-2 border-gray-300'}`}>
              {logToCRM && <Check className="w-3.5 h-3.5 text-white" />}
            </div>
            <span className="text-sm font-medium text-gray-700">Log to CRM</span>
          </label>
          {sent ? (
            <div className="flex items-center gap-2 justify-center py-3 bg-green-50 rounded-lg" data-testid="action-sent-success">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-green-700">Action completed & logged to CRM</span>
            </div>
          ) : (
            <button
              onClick={handleSend}
              disabled={sending}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="send-action-btn"
            >
              {sending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                'Send & Complete Action'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AccountDetail() {
  const { id } = useParams();
  const { selectedIndustry } = useIndustry();
  const { opportunities, accountDetails } = getIndustryData(selectedIndustry);
  const opportunity = opportunities.find(opp => opp.id === Number(id));
  const detail = accountDetails[Number(id)];
  const [activeAction, setActiveAction] = useState(0);
  const [actionTaken, setActionTaken] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [completedPills, setCompletedPills] = useState({});
  const [allClosed, setAllClosed] = useState(false);
  const [showTakeActionModal, setShowTakeActionModal] = useState(false);

  if (!opportunity || !detail) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500">Account not found.</p>
        <Link to="/dashboard/opportunities" className="text-blue-600 hover:underline text-sm mt-2 inline-block">Back to Opportunities</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="account-detail-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm" data-testid="breadcrumb">
        <Link to="/dashboard/opportunities" className="text-gray-500 hover:text-gray-700 transition-colors">Opportunities</Link>
        <span className="text-gray-400">/</span>
        <span className="font-semibold text-gray-900">{opportunity.accountName}</span>
      </nav>

      {/* Main Card: Header + Deal Summary + Pending Actions */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="account-header">
        {/* Header */}
        <div className="p-8 pb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-lg ${opportunity.avatarColor} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                  {opportunity.avatar}
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{opportunity.accountName}</h2>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${detail.stageColor}`}>
                  {detail.stage}
                </span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500">Deal Value:</span>
                  <span className="font-bold text-gray-900">${opportunity.dealValue.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-gray-500">Close Date:</span>
                  <span className="font-bold text-gray-900">{detail.closeDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full ${detail.poc.avatarColor} flex items-center justify-center text-white text-[10px] font-bold`}>
                    {detail.poc.avatar}
                  </div>
                  <span className="font-medium text-gray-900">{detail.poc.name}</span>
                </div>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex flex-col gap-2" data-testid="action-buttons">
              {actionTaken && acknowledged ? (
                <div className="flex items-center gap-2 px-6 py-2.5 bg-green-100 text-green-700 text-sm font-semibold rounded-lg">
                  <Check className="w-4 h-4" /> All Done
                </div>
              ) : (
                <>
                  {actionTaken ? (
                    <div className="flex items-center gap-2 px-6 py-2.5 bg-green-100 text-green-700 text-sm font-semibold rounded-lg" data-testid="take-action-done">
                      <Check className="w-4 h-4" /> Action Taken
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowTakeActionModal(true)}
                      className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                      data-testid="take-action-btn"
                    >
                      Take Action
                    </button>
                  )}
                  {acknowledged ? (
                    <div className="flex items-center gap-2 px-6 py-2.5 bg-blue-50 text-blue-700 text-sm font-semibold rounded-lg" data-testid="acknowledge-done">
                      <Check className="w-4 h-4" /> Acknowledged
                    </div>
                  ) : (
                    <button
                      onClick={() => setAcknowledged(true)}
                      className="px-6 py-2.5 border border-gray-300 text-sm font-semibold text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid="acknowledge-btn"
                    >
                      Acknowledge
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 mx-8" />

        {/* Deal Summary */}
        {detail.dealSummary && (
          <div className="px-8 py-6" data-testid="deal-summary">
            <h3 className="text-base font-bold text-gray-900 mb-3">Deal Summary</h3>
            <p className="text-sm text-gray-600 leading-relaxed">{detail.dealSummary}</p>
          </div>
        )}

        {/* Pending Actions */}
        {detail.pendingActions && !allClosed && (
          <div className="px-8 pb-8" data-testid="pending-actions">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900">Pending Actions</h3>
              <button
                onClick={() => setAllClosed(true)}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="close-all-btn"
              >
                Close All
              </button>
            </div>
            {/* Action Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              {detail.pendingActions.items.map((action, idx) => {
                const isCompleted = completedPills[idx];
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      if (!isCompleted) setActiveAction(idx);
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isCompleted
                        ? 'bg-green-100 text-green-700 line-through'
                        : idx === activeAction
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`action-pill-${idx}`}
                  >
                    {isCompleted && <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
                    {action.label}
                  </button>
                );
              })}
            </div>
            {/* Action Description */}
            <div className="bg-gray-50 rounded-lg p-4" data-testid="action-description">
              {completedPills[activeAction] ? (
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-700">This action has been completed and pushed to CRM.</span>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">{detail.pendingActions.items[activeAction]?.description}</p>
                  <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full ${detail.pendingActions.assignedTo.avatarColor} flex items-center justify-center text-white text-[9px] font-bold`}>
                        {detail.pendingActions.assignedTo.avatar}
                      </div>
                      <span className="text-xs text-gray-500">
                        Assigned to: <span className="font-semibold text-gray-700">{detail.pendingActions.assignedTo.name}</span> · {detail.pendingActions.assignedTo.role}
                      </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {allClosed && (
          <div className="px-8 pb-8" data-testid="all-actions-closed">
            <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
              <Check className="w-5 h-5 text-green-600" />
              <span className="text-sm font-semibold text-green-700">All pending actions have been closed and pushed to CRM.</span>
            </div>
          </div>
        )}
      </div>

      {/* Two Column Layout: Timeline + Contacts */}
      <div className="grid grid-cols-5 gap-6">
        {/* Activity Timeline - 3 cols */}
        <div className="col-span-3 bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="activity-timeline">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Activity Timeline</h3>
          <div className="space-y-0">
            {detail.timeline.map((event, idx) => {
              const iconConfig = timelineIcons[event.type] || timelineIcons.meeting;
              const IconComp = iconConfig.icon;
              const isLast = idx === detail.timeline.length - 1;
              return (
                <div key={event.id} className="flex gap-4" data-testid={`timeline-event-${event.id}`}>
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full ${iconConfig.bg} flex items-center justify-center flex-shrink-0`}>
                      <IconComp className={`w-4.5 h-4.5 ${iconConfig.color}`} />
                    </div>
                    {!isLast && <div className="w-px flex-1 bg-gray-200 my-1 min-h-[24px]" />}
                  </div>
                  <div className={`flex-1 ${!isLast ? 'pb-6' : ''}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">{event.title}</h4>
                        {event.from && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <div className={`w-5 h-5 rounded-full ${event.fromAvatarColor} flex items-center justify-center text-white text-[8px] font-bold`}>
                              {event.fromAvatar}
                            </div>
                            <span className="text-xs text-gray-500">{event.type === 'inbound_email' ? 'From:' : 'Sent by:'} {event.from}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0 pt-0.5">
                        <Clock className="w-3 h-3" />
                        <span>{event.time}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2 leading-relaxed">{event.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Contacts - 2 cols */}
        <div className="col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-6 self-start" data-testid="contacts-panel">
          <h3 className="text-lg font-bold text-gray-900 mb-5">Contacts ({detail.contacts.length})</h3>
          <div className="space-y-3">
            {detail.contacts.map((contact) => (
              <div key={contact.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100" data-testid={`contact-${contact.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${contact.avatarColor} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                    {contact.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{contact.name}</p>
                    <p className="text-xs text-gray-500">{contact.title}</p>
                  </div>
                </div>
                {contact.badge && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-600 border border-green-200" data-testid={`badge-${contact.id}`}>
                    {contact.badge}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showTakeActionModal && (
        <TakeActionModal
          opportunity={opportunity}
          detail={detail}
          onClose={() => setShowTakeActionModal(false)}
          onDone={() => setActionTaken(true)}
        />
      )}

      <DealChat opportunity={opportunity} detail={detail} industry={selectedIndustry} />
    </div>
  );
}
